import React, { useState } from 'react';
import '../styles/PotteryModal.css';
import ImageLightbox from './ImageLightbox';

const PotteryModal = ({ item, currentLang, onClose }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    if (!item) return null;

    const stockText = item.stock === 'in_stock'
        ? (currentLang === 'ar' ? 'متوفر' : 'In Stock')
        : (currentLang === 'ar' ? 'غير متوفر' : 'Out of Stock');
    const stockClass = item.stock === 'in_stock' ? 'stock-in' : 'stock-out';

    const methodText = {
        'handmade': currentLang === 'ar' ? 'يدوي' : 'Handmade',
        'wheel': currentLang === 'ar' ? 'عجلة خزاف' : 'Potter\'s Wheel',
        'free_form': currentLang === 'ar' ? 'تشكيل حر' : 'Free Form'
    };

    const images = item.images && item.images.length > 0 
        ? item.images 
        : [item.image || 'pottery1.jpg'];

    const getImageSrc = (img) => {
        // التحقق من نوع الصورة
        if (item.imageType === 'base64' || (img && typeof img === 'string' && img.startsWith('data:image'))) {
            return img;
        }
        // إذا كانت الصورة من public folder
        if (img && typeof img === 'string') {
            return `${process.env.PUBLIC_URL}/${img}`;
        }
        return img;
    };

    const handleImageClick = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const whatsappMessage = encodeURIComponent(
        (currentLang === 'ar' ? 'أريد شراء: ' : 'I want to buy: ') + 
        (currentLang === 'ar' ? item.titleAr : item.titleEn)
    );

    return (
        <div className="pottery-modal" onClick={onClose}>
            <div className="pottery-modal-content" onClick={(e) => e.stopPropagation()}>
                <span className="pottery-modal-close" onClick={onClose}>&times;</span>
                <div className="pottery-modal-body">
                    <div className="pottery-modal-images">
                        {images.map((img, index) => (
                            <img
                                key={index}
                                src={getImageSrc(img)}
                                alt={currentLang === 'ar' ? item.titleAr : item.titleEn}
                                className="pottery-modal-image"
                                loading="lazy"
                                onClick={() => handleImageClick(index)}
                                style={{ cursor: 'pointer' }}
                                onError={(e) => {
                                    console.error('Image failed to load:', img);
                                    if (item.imageType !== 'base64') {
                                        e.target.src = `${process.env.PUBLIC_URL}/pottery1.jpg`;
                                    }
                                }}
                            />
                        ))}
                    </div>
                    {lightboxOpen && (
                        <ImageLightbox
                            images={images}
                            currentIndex={lightboxIndex}
                            onClose={() => setLightboxOpen(false)}
                            imageType={item.imageType || 'file'}
                        />
                    )}
                    <h2 className="pottery-modal-title">
                        {currentLang === 'ar' ? item.titleAr : item.titleEn}
                    </h2>
                    <div className="pottery-modal-price">
                        {item.price || 'N/A'} {item.currency || 'KWD'}
                    </div>
                    <div className={`pottery-modal-stock-badge ${stockClass}`}>
                        {stockText} {item.quantity ? `(${item.quantity})` : ''}
                    </div>
                    <div className="pottery-modal-description">
                        {currentLang === 'ar' ? item.descriptionAr : item.descriptionEn}
                    </div>

                    <div className="pottery-modal-specs">
                        <h3>
                            {currentLang === 'ar' ? 'المواصفات الفنية' : 'Technical Specifications'}
                        </h3>
                        <div className="specs-grid">
                            <div className="spec-item">
                                <span className="spec-label">
                                    {currentLang === 'ar' ? 'الطول' : 'Length'}
                                </span>
                                <span className="spec-value">
                                    {item.length ? `${item.length} ${currentLang === 'ar' ? 'سم' : 'cm'}` : (currentLang === 'ar' ? 'غير متوفر' : 'N/A')}
                                </span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">
                                    {currentLang === 'ar' ? 'العرض' : 'Width'}
                                </span>
                                <span className="spec-value">
                                    {item.width ? `${item.width} ${currentLang === 'ar' ? 'سم' : 'cm'}` : (currentLang === 'ar' ? 'غير متوفر' : 'N/A')}
                                </span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">
                                    {currentLang === 'ar' ? 'الوزن' : 'Weight'}
                                </span>
                                <span className="spec-value">
                                    {item.weight ? `${item.weight} ${currentLang === 'ar' ? 'جم' : 'g'}` : (currentLang === 'ar' ? 'غير متوفر' : 'N/A')}
                                </span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">
                                    {currentLang === 'ar' ? 'المواد المستخدمة' : 'Materials'}
                                </span>
                                <span className="spec-value">
                                    {item.materials || (currentLang === 'ar' ? 'غير متوفر' : 'N/A')}
                                </span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">
                                    {currentLang === 'ar' ? 'طريقة التصنيع' : 'Manufacturing Method'}
                                </span>
                                <span className="spec-value">
                                    {item.method ? (methodText[item.method] || item.method) : (currentLang === 'ar' ? 'غير متوفر' : 'N/A')}
                                </span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">
                                    {currentLang === 'ar' ? 'الألوان / التشطيبات' : 'Colors / Finishes'}
                                </span>
                                <span className="spec-value">
                                    {item.colors || (currentLang === 'ar' ? 'غير متوفر' : 'N/A')}
                                </span>
                            </div>
                            <div className="spec-item">
                                <span className="spec-label">
                                    {currentLang === 'ar' ? 'تاريخ الصنع' : 'Date Made'}
                                </span>
                                <span className="spec-value">
                                    {item.date || (currentLang === 'ar' ? 'غير متوفر' : 'N/A')}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="pottery-modal-whatsapp">
                        {item.stock === 'in_stock' ? (
                            <a
                                href={`https://wa.me/96560406068?text=${whatsappMessage}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="whatsapp-buy-btn"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                                <span>{currentLang === 'ar' ? 'شراء عبر واتساب' : 'Buy via WhatsApp'}</span>
                            </a>
                        ) : (
                            <button className="whatsapp-buy-btn" disabled>
                                {currentLang === 'ar' ? 'غير متوفر حالياً' : 'Currently Unavailable'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PotteryModal;

